import featureFlags from '@enums/feature-flags';
import routeNames from '@enums/route-names';
import { get } from 'lodash';
import store from '@/js/store';
import AdminPage from './admin.vue';
import guardsMap from './admin-guards-map';
import RateCard from '../supplier/components/rate-card/rate-card.vue';
import FreeGiftsViewer from './components/free-gifts-viewer.vue';
import LoyaltyPointsViewer from './components/loyalty-points-viewer.vue';
import UserPermissions from './components/user-permissions/user-permissions.vue';
import ClientSystemsTesting from './components/client-systems-testing/client-systems-testing.vue';
import RedisBull from './components/redis-bull/redis-bull.vue';
import FeatureToggler from './components/feature-toggler/feature-toggler.vue';
import ConfigEditor from './components/config-editor.vue';
import OfferPresetsAdmin from './components/offer-presets/offer-presets-admin.vue';

const defaultBeforeEnter = (to, from) => {
    return guardsMap[to.name](to, from);
};

const routes = [
    {
        path: '/admin',
        id: 'admin',
        featureToggle: 'canAccessAdminAreaOrClientSystemsTesting',
        omitFromNav: true,
        component: AdminPage,
        linkText: 'navBar.admin',
        cssClasses: ['admin-toolbar-item'],
        meta: {
            canAccess: featureFlags.canAccessAdminAreaOrClientSystemsTesting,
            tabTitle: 'navBar.admin',
        },
        async beforeEnter() {
            // Users with canAccessClientSystemsTesting permissions only (without canAccessAdminArea)
            // can enter the admin shell but should not load standard admin data.
            if (!store.state.clientConfig.toggleLogic[featureFlags.canAccessAdminArea]) return true;

            await store.dispatch('rateCards/fetchRateCards', {
                params: {
                    sortBy: 'lastUpdatedOn',
                    sortDirection: 'descending',
                },
            });

            await store.dispatch('freeGifts/fetchFreeGifts', {
                params: {
                    sortBy: 'lastUpdatedOn',
                    sortDirection: 'descending',
                },
            });

            await store.dispatch('loyaltyPoints/fetchLoyaltyPoints', {
                params: {
                    sortBy: 'lastUpdatedOn',
                    sortDirection: 'descending',
                },
            });

            await store.dispatch('userProfiles/fetchUserProfiles');
            await store.dispatch('clientConfig/loadMongoConfig');
            return true;
        },
        children: [
            // Default route to ensure that all '/admin' requests are redirected to the '/admin/freeGifts'.
            {
                path: '',
                name: 'admin',
                redirect: () => {
                    const { toggleLogic } = store.state.clientConfig;
                    const isTestingOnlyUser =
                        !toggleLogic[featureFlags.canAccessAdminArea] &&
                        toggleLogic[featureFlags.canAccessClientSystemsTesting];

                    return {
                        name: isTestingOnlyUser
                            ? routeNames.clientSystemsTesting
                            : get(
                                  toggleLogic,
                                  featureFlags.defaultAdminTab,
                                  routeNames.userPermissions
                              ),
                    };
                },
            },
            {
                path: 'free-gifts',
                meta: { canAccess: featureFlags.canAccessAdminArea },
                name: routeNames.freeGiftsView,
                component: FreeGiftsViewer,
                beforeEnter: defaultBeforeEnter,
            },
            {
                path: 'offer-presets',
                meta: { canAccess: featureFlags.canManageOfferMechanicPresets },
                name: routeNames.offerPresetsAdmin,
                component: OfferPresetsAdmin,
                beforeEnter: defaultBeforeEnter,
            },
            {
                path: 'rate-card',
                meta: { canAccess: featureFlags.canModifyRateCards },
                name: routeNames.rateCardView,
                component: RateCard,
                beforeEnter: defaultBeforeEnter,
            },
            {
                path: 'loyalty-points',
                meta: { canAccess: featureFlags.canAccessAdminArea },
                name: routeNames.loyaltyPointsView,
                component: LoyaltyPointsViewer,
                beforeEnter: defaultBeforeEnter,
            },
            {
                path: 'userPermissions',
                meta: { canAccess: featureFlags.canAccessAdminArea },
                name: routeNames.userPermissions,
                component: UserPermissions,
                beforeEnter: defaultBeforeEnter,
            },
            {
                path: 'client-systems-testing',
                meta: { canAccess: featureFlags.canAccessClientSystemsTesting },
                name: routeNames.clientSystemsTesting,
                component: ClientSystemsTesting,
                beforeEnter: defaultBeforeEnter,
            },
            {
                path: 'redis-bull',
                meta: { canAccess: featureFlags.canAccessAdminTools },
                name: routeNames.redisBull,
                component: RedisBull,
                beforeEnter: defaultBeforeEnter,
            },
            // {
            //     path: 'execution-errors',
            //     meta: { canAccess: featureFlags.canAccessAdminTools },
            //     name: routeNames.executionErrors,
            //     beforeEnter: defaultBeforeEnter,
            // },
            {
                path: 'feature-toggler',
                meta: { canAccess: featureFlags.canAccessAdminTools },
                name: routeNames.featureToggler,
                component: FeatureToggler,
                beforeEnter: defaultBeforeEnter,
            },
            {
                path: 'config-editor',
                meta: { canAccess: featureFlags.canAccessAdminTools },
                name: routeNames.configEditorView,
                component: ConfigEditor,
                beforeEnter: defaultBeforeEnter,
            },
        ],
    },
];

export default routes;
